import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Container,
  Fade,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MaterialReactTable } from 'material-react-table';
import { useApi } from '../../hooks/useApi';
import { LIST_LIMIT, botOptions, formatCreated, isComplete, reportPath, searchReports } from './botReports';
import { useBotReports } from './useBotReports';

// The reports the bots have written, newest first. Click a report to read it.
const ACCOUNT_KEY = 'botReportsAccount';

const readStored = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (ex) {
    return null;
  }
};
const store = (key, value) => {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch (ex) {
    // Remembering the choice is a convenience only.
  }
};

const columns = [
  {
    accessorKey: 'createdUtc',
    header: 'Saved',
    size: 170,
    Cell: ({ cell }) => formatCreated(cell.getValue()),
  },
  { accessorKey: 'account', header: 'Account', size: 110 },
  { accessorKey: 'botId', header: 'Bot', size: 200 },
  { accessorKey: 'title', header: 'Title', size: 360 },
  { accessorKey: 'deviceType', header: 'Device type', size: 110 },
  { accessorKey: 'requestedBy', header: 'Requested by', size: 140 },
  {
    id: 'status',
    header: 'Status',
    size: 110,
    accessorFn: (r) => (isComplete(r) ? 'Complete' : 'Incomplete'),
    Cell: ({ cell }) => (
      <Chip size="small" label={cell.getValue()} color={cell.getValue() === 'Complete' ? 'success' : 'warning'} variant="outlined" />
    ),
  },
];

function BotReportsPage() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);       // null = every account the caller may see
  const [botId, setBotId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [accountError, setAccountError] = useState(null);

  const { loading, error, items, reload } = useBotReports({ account: account?.code || '', botId, from, to });

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setIsLoadingAccounts(true);
        const response = await jApi.request('GET', 'cma/account');
        const list = (response.data || []).slice().sort((a, b) => a.code.localeCompare(b.code));
        setAccounts(list);
        const savedCode = readStored(ACCOUNT_KEY);
        setAccount(list.find(a => a.code === savedCode) || null);
      } catch (ex) {
        console.error('Failed to load accounts:', ex);
        setAccountError(ex?.toString?.() || 'Failed to load accounts');
      } finally {
        setIsLoadingAccounts(false);
      }
    };
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccountChange = (_event, value) => {
    setAccount(value);
    store(ACCOUNT_KEY, value?.code || '');
  };

  // The bot filter offers the bots in the current list and always the one chosen.
  const bots = useMemo(() => {
    const names = botOptions(items);
    return botId && !names.includes(botId) ? [...names, botId].sort((a, b) => a.localeCompare(b)) : names;
  }, [items, botId]);

  const shown = useMemo(() => searchReports(items, text), [items, text]);

  const subtitle = loading
    ? 'Loading reports...'
    : `${shown.length} report${shown.length === 1 ? '' : 's'}${shown.length !== items.length ? ` of ${items.length}` : ''}`;

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
                  '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) } }}>
                  <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>
                Bot Reports
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Paper sx={{ p: 2, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Bot Reports</Typography>
                  <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 9 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                    <Autocomplete
                      options={accounts}
                      getOptionLabel={(a) => `${a.name} (${a.code})`}
                      value={account}
                      onChange={handleAccountChange}
                      isOptionEqualToValue={(a, b) => a.code === b.code}
                      size="small"
                      loading={isLoadingAccounts}
                      sx={{ width: { xs: '100%', sm: 260 } }}
                      renderInput={(params) => <TextField {...params} label="Account" placeholder="All accounts" />}
                    />
                    <TextField
                      select
                      size="small"
                      label="Bot"
                      value={botId}
                      onChange={(e) => setBotId(e.target.value)}
                      sx={{ width: { xs: '100%', sm: 220 } }}
                    >
                      <MenuItem value="">All bots</MenuItem>
                      {bots.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                    </TextField>
                    <TextField
                      size="small"
                      type="date"
                      label="From"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ width: { xs: '100%', sm: 160 } }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      label="To"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ width: { xs: '100%', sm: 160 } }}
                    />
                    <TextField
                      size="small"
                      label="Search"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      sx={{ width: { xs: '100%', sm: 180 } }}
                    />
                    <Tooltip title="Refresh">
                      <span>
                        <IconButton onClick={reload} disabled={loading} size="small" aria-label="Refresh">
                          <RefreshIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
              {(loading || isLoadingAccounts) && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
            </Paper>
          </Grid>

          {(accountError || error) && (
            <Grid size={12}>
              <Alert severity="error">{accountError || error}</Alert>
            </Grid>
          )}

          {!error && !loading && items.length >= LIST_LIMIT && (
            <Grid size={12}>
              <Alert severity="info">
                Showing the newest {LIST_LIMIT} reports. Choose an account, a bot or a date range to narrow the list.
              </Alert>
            </Grid>
          )}

          {!error && !loading && items.length === 0 && (
            <Grid size={12}>
              <Alert severity="info">No bot reports match these filters.</Alert>
            </Grid>
          )}

          {!error && items.length > 0 && (
            <Grid size={12}>
              <MaterialReactTable
                columns={columns}
                data={shown}
                enableDensityToggle={false}
                initialState={{ sorting: [{ id: 'createdUtc', desc: true }], pagination: { pageSize: 25, pageIndex: 0 } }}
                muiTableBodyRowProps={({ row }) => ({
                  hover: true,
                  onClick: () => navigate(reportPath(row.original.id)),
                  sx: { cursor: 'pointer' },
                })}
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default BotReportsPage;
